import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Tag, Package, X } from 'lucide-react';

const OfferModal = ({ onClose, onAdd, onEdit, editingOffer, products = [] }) => {
  const [formData, setFormData] = useState({
    name: editingOffer?.name || '',
    description: editingOffer?.description || '',
    price: editingOffer?.price || '',
    originalPrice: editingOffer?.originalPrice || '',
    products: editingOffer?.products || [{ name: '', weight: '', unit: 'grams' }]
  });
  const [error, setError] = useState('');

  const addProduct = () => {
    if (formData.products.length < 15) {
      setFormData({
        ...formData,
        products: [...formData.products, { name: '', weight: '', unit: 'grams' }]
      });
    }
  };

  const removeProduct = (index) => {
    if (formData.products.length > 1) {
      const newProducts = formData.products.filter((_, i) => i !== index);
      setFormData({ ...formData, products: newProducts });
    }
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...formData.products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    setFormData({ ...formData, products: newProducts });
  };

  const handleSubmit = () => {
    setError('');
    if (!formData.name || !formData.price) {
      setError('Offer name and combo price are required.');
      return;
    }
    const validProducts = formData.products.filter(p => p.name && p.weight);
    if (validProducts.length === 0) {
      setError('Add at least one product to the combo.');
      return;
    }
    const comboPrice = Number(formData.price);
    const originalPrice = formData.originalPrice ? Number(formData.originalPrice) : null;
    if (originalPrice != null && comboPrice >= originalPrice) {
      setError('Combo price must be less than original price.');
      return;
    }
    const offerData = {
      name: formData.name,
      description: formData.description,
      price: comboPrice,
      originalPrice: originalPrice,
      products: validProducts,
      items: validProducts.length
    };
    if (editingOffer) {
      onEdit(editingOffer.id, offerData);
    } else {
      onAdd(offerData);
    }
  };

  return (
    <div className="ui-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">{editingOffer ? 'Edit Offer' : 'Create New Offer'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold mb-2">Offer Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg outline-none focus:border-green-500"
                placeholder="Enter offer name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg outline-none focus:border-green-500"
                placeholder="Enter description"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Combo Price (₹)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg outline-none focus:border-green-500"
                placeholder="Enter combo price"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Original Price (₹)</label>
              <input
                type="number"
                value={formData.originalPrice}
                onChange={(e) => setFormData({...formData, originalPrice: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg outline-none focus:border-green-500"
                placeholder="Enter original price"
              />
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">Products in Combo (Up to 15)</h4>
              <button
                type="button"
                onClick={addProduct}
                disabled={formData.products.length >= 15}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 font-medium ${
                  formData.products.length >= 15
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                <Plus size={16} />
                Add Product
              </button>
            </div>
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {formData.products.map((product, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="font-medium text-gray-700">Product {index + 1}</h5>
                    {formData.products.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-6">
                      <select
                        value={product.productId != null ? String(product.productId) : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const selected = products.find(
                            (p) => String(p.id) === value
                          );
                          const newProducts = [...formData.products];
                          newProducts[index] = {
                            ...newProducts[index],
                            productId: value || undefined,
                            name: selected?.name || '',
                            weight: selected ? (newProducts[index].weight || selected.weight || '') : newProducts[index].weight,
                            unit: selected ? (newProducts[index].unit || selected.stockUnit || selected.weightUnit || 'grams') : newProducts[index].unit,
                          };
                          setFormData({ ...formData, products: newProducts });
                        }}
                        className="w-full px-3 py-2 border rounded outline-none focus:border-green-500"
                      >
                        <option value="">
                          {products.length === 0
                            ? 'No products — add some in Products first'
                            : 'Select product'}
                        </option>
                        {products.map((p) => (
                          <option key={p.id} value={String(p.id)}>
                            {p.name || `Product #${p.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <input
                        type="number"
                        value={product.weight}
                        onChange={(e) => handleProductChange(index, 'weight', e.target.value)}
                        className="w-full px-3 py-2 border rounded outline-none focus:border-green-500"
                        placeholder="Weight"
                      />
                    </div>
                    <div className="col-span-3">
                      <select
                        value={product.unit}
                        onChange={(e) => handleProductChange(index, 'unit', e.target.value)}
                        className="w-full px-3 py-2 border rounded outline-none focus:border-green-500"
                      >
                        <option value="grams">grams</option>
                        <option value="kg">kg</option>
                        <option value="pieces">pieces</option>
                        <option value="ml">ml</option>
                        <option value="liters">liters</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold"
            >
              {editingOffer ? 'Update Offer' : 'Create Offer'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Offers = ({ offers = [], loading = false, onAdd, onEdit, onDelete, products = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOffer(null);
  };

  return (
    <div className="ui-page">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Offers & Combo Packs
            </h1>
            <p className="text-gray-600 mt-2">Create attractive combo deals for your customers</p>
          </div>
          
          <button 
            onClick={() => setShowModal(true)}
            className="ui-btn-primary"
          >
            <Plus size={20} />
            Add Offer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {offers.map(offer => (
          <div key={offer.id} className="ui-card ui-card-pad ui-card-hover">
            <div>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-700 shadow-lg">
                    <Tag className="text-white" size={18} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{offer.name}</h3>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleEdit(offer)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    onClick={() => onDelete && onDelete(offer.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="p-1 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200">
                    <Package size={14} className="text-gray-500" />
                  </div>
                  <span className="text-sm font-medium">
                    {offer.items ?? offer.products?.length ?? 0} items included
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-3 font-medium">{offer.description}</p>
                
                {offer.products && offer.products.length > 0 && (
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl">
                    <h5 className="text-xs font-bold text-gray-700 mb-2">Products:</h5>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {offer.products.slice(0, 6).map((product, idx) => (
                        <div key={idx} className="text-gray-700 font-medium">
                          {product.name} - {product.weight}{product.unit}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 font-semibold">Combo Price</span>
                  <span className="text-xl font-bold text-orange-600">
                    ₹{Number.isFinite(offer.price) ? offer.price : 0}
                  </span>
                </div>
                {offer.originalPrice && (
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-600 font-medium">Individual Price</span>
                    <span className="text-sm text-gray-600 line-through font-medium">
                      ₹{Number.isFinite(offer.originalPrice) ? offer.originalPrice : 0}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {showModal && (
        <OfferModal 
          onClose={handleCloseModal}
          onAdd={(offerData) => {
            onAdd && onAdd(offerData);
            handleCloseModal();
          }}
          onEdit={(id, offerData) => {
            onEdit && onEdit(id, offerData);
            handleCloseModal();
          }}
          editingOffer={editingOffer}
          products={products}
        />
      )}
    </div>
  );
};

export default Offers;